import { Button } from "@mui/material";
import { LottieOptions, useLottie } from "lottie-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { textGradient } from "../GradientBox";
import {
  desktopQuery,
  DeviceWidth,
  mobileQuery,
  useDeviceWidthQuery,
} from "../MediaQuery";
import { Navigation } from "../navigation/Navigation";
import { SizedBox } from "../SizedBox";
import { language, translation } from "../translations";
import { nl2br } from "../utils";
import { SlantedSection } from "./SlantedSection";
import * as trackerAnimationDataPl from "./tracker-lottie-animation-pl.json";
import * as trackerAnimationDataEn from "./tracker-lottie-animation-en.json";

export const HomeHeader: React.FC<{
  includeNavigation: boolean;
  learnMore: boolean;
}> = (props) => {
  const deviceWidth = useDeviceWidthQuery();

  if (deviceWidth == DeviceWidth.desktop) {
    return (
      <header>
        <Wrapper>
          <Background />
          <GainHealth />
          <Woman />
          <Phone />
          {props.includeNavigation && <Navigation />}
        </Wrapper>
        <Endorsement />
        {props.learnMore && <LearnMore />}
        <SizedBox height={46} />
      </header>
    );
  } else {
    return (
      <header>
        {props.includeNavigation && <Navigation />}
        <Wrapper>
          <Background />
          <GainHealth />
          <Woman />
          <Phone />
        </Wrapper>
        <Endorsement />
        {props.learnMore && <LearnMore />}
      </header>
    );
  }
};

const Wrapper = styled.div`
  position: relative;

  height: 720px;

  ${mobileQuery`
   height: 400px;
  `}
`;

const Background = styled.div`
  position: absolute;
  left: 0;
  right: 0;

  height: 100%;
  background-image: url("/home/header/blurred-park.jpg");
  background-repeat: no-repeat;
  background-position: center center;
  background-size: auto 100%;
`;

const GainHealthWrapper = styled.div`
  position: absolute;
  left: 0px;
  right: 0px;
  top: 188px;

  ${mobileQuery`
    top: 0px;
  `}
`;

const GainHealthHeader = styled.h1`
  font-size: 56px;
  font-weight: bold;
  background: ${textGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0px;

  ${mobileQuery`
    font-size: 24px;
  `}
`;

const GainHealthSubtitle = styled.h2`
  font-size: 24px;
  font-weight: 500;
  color: #333333;
  margin: 0px;

  ${mobileQuery`
    font-size: 16px;
  `}
`;

const GainHealthContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: left;
  margin-right: 120px;

  ${mobileQuery`
    margin-right: 180px;
  `}
`;

const GainHealth = () => (
  <GainHealthWrapper>
    <SlantedSection
      offset={-1000}
      height={243}
      width={1300}
      justifyContent="right"
      alignItems="center"
      color="white"
      skewLeft={false}
    >
      <GainHealthContent>
        <GainHealthHeader>
          {nl2br(translation.gainHealthHeader)}
        </GainHealthHeader>
        <SizedBox height={10} />
        <GainHealthSubtitle>
          {nl2br(translation.gainHealthSubtitle)}
        </GainHealthSubtitle>
      </GainHealthContent>
    </SlantedSection>
  </GainHealthWrapper>
);

const Woman = styled.div`
  position: absolute;
  left: 0;
  right: 0;

  height: 100%;
  background-image: url("/home/header/woman.png");
  background-repeat: no-repeat;
  background-position: calc(50% + 260px) center;
  background-size: auto 100%;

  ${mobileQuery`
    background-position: calc(50% + 90px) center;
  `}
`;

const PhonePositioner = styled.div`
  display: flex;
  left: 0;
  right: 0;
  bottom: 0;

  justify-content: center;
  margin-left: -650px;

  ${mobileQuery`
    margin-left: 0;
    margin-right: -40%;
  `}
`;

const PhoneWrapper = styled.div`
  position: absolute;
  bottom: 0;
  height: 264px;
  width: 402px;

  ${mobileQuery`
    height: 138px;
    width: 210px;
  `}
`;

const PhoneBackground = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0px;

  height: 100%;
  background-image: url("/home/header/phone.png");
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 100% 100%;
`;

const PhoneTrackerWrapper = styled.div`
  position: absolute;
  left: 4%;
  right: 4%;
  top: 12%;
  bottom: 0px;
  height: 92%;
  width: 92%;
`;

const RewindWrapper = styled.div`
  position: absolute;
  left: 8%;
  bottom: 7%;
  height: 77px;
  width: 77px;

  ${mobileQuery`
    height: 40px;
    width: 40px;
  `}
`;

const Phone = () => {
  const [showButton, setButton] = useState(false);

  const onAnimationComplete = () => {
    setButton(true);
  };

  const lottieOptions: LottieOptions = {
    loop: false,
    autoplay: true,
    animationData: JSON.parse(JSON.stringify(
      language == "pl" ? trackerAnimationDataPl : trackerAnimationDataEn)),
    onComplete: onAnimationComplete,
  };

  const { View: TrackerAnimation, goToAndPlay } = useLottie(lottieOptions);

  const rewind = () => {
    goToAndPlay(0);
    setButton(false);
  };

  const Rewind = () => {
    return (
      <img
        src="/home/header/rewind.png"
        onClick={rewind}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
    );
  };

  return (
    <PhonePositioner>
      <PhoneWrapper>
        <PhoneBackground />
        <PhoneTrackerWrapper>{TrackerAnimation}</PhoneTrackerWrapper>
        {showButton && (
          <RewindWrapper>
            <Rewind />
          </RewindWrapper>
        )}
      </PhoneWrapper>
    </PhonePositioner>
  );
};

const Endorsement = () => {
  const deviceWidth = useDeviceWidthQuery();

  return (
    <div
      style={{
        marginTop: deviceWidth === DeviceWidth.desktop ? -110 : 0,
      }}
    >
      <SlantedSection
        offset={180}
        height={deviceWidth === DeviceWidth.desktop ? 240 : 80}
        width={650}
        justifyContent="center"
        alignItems="center"
        color="white"
        skewLeft={false}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <EndorsementText>{translation.ptmszEndorsement}</EndorsementText>
          <EndorsementLogo src="/home/header/ptmsz.png" />
        </div>
      </SlantedSection>
    </div>
  );
};

const EndorsementText = styled.h3`
  font-size: 48px;
  font-weight: bold;
  font-style: italic;
  background: ${textGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-left: 60px;
  margin-top: 0px;
  margin-bottom: 24px;

  ${mobileQuery`
    font-size: 24px;
  `}
`;

const EndorsementLogo = styled.img`
  height: 32px;

  ${mobileQuery`
    height: 23px;
  `}
`;

const LearnMoreWrapper = styled.div`
  display: flex;

  justify-content: center;

  ${desktopQuery`
    margin-left: -650px;
    margin-top: -100px;
    margin-bottom: 30px;
  `}
`;

const LearnMore = () => (
  <LearnMoreWrapper>
    <Button
      variant="contained"
      color="primary"
      style={{
        boxShadow: "0px 1px 5px 0px rgba(0,0,0,0.2)",
        margin: 0,
        textAlign: "center",
        display: "inline-block",
      }}
      component={Link}
      to={"/"}
    >
      {translation.learnMore}
    </Button>
  </LearnMoreWrapper>
);
